import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, fromEvent, interval, merge } from 'rxjs';
import { exhaustMap, filter, startWith, takeWhile } from 'rxjs/operators';

const POLL_INTERVAL_MS = 5 * 60_000; // 5 min
const FETCH_TIMEOUT_MS = 10_000; // 10 s — bound a hung request
const HASHED_MAIN_BUNDLE = /^main-[A-Z0-9]+\.js$/i;

/**
 * Detects when a new build has been deployed while the app is open and
 * surfaces the staleness as a signal the shell can read to prompt the
 * user to reload.
 *
 * Why this exists on top of the other cache-busting mechanisms:
 *   - `handleRouterNavigationError` (chunk-reload.utils) is *reactive* —
 *     it only fires when a `loadComponent` chunk import fails. A user
 *     who never navigates to a route whose chunk has changed keeps
 *     running the old build forever.
 *   - The tombstone service worker only intervenes on browsers that
 *     ever installed the legacy Angular SW. Returning users who joined
 *     after the SW was removed don't have it, and their HTTP-cached
 *     `index.html` (from before `Cache-Control: no-store` was applied,
 *     or in transit between a deploy and CloudFront invalidation
 *     propagation) keeps serving the old chunk hashes.
 *
 * Approach: poll `/index.html` with `fetch(url, { cache: 'reload' })`.
 * That option forces the browser to bypass its HTTP cache and go
 * straight to network — the only standards-compliant way to get past
 * a long-cached entry without unregistering a service worker. Extract
 * the hashed `main-XXXXX.js` filename from the response and compare to
 * the bundle the page currently has loaded; both are emitted by the
 * same Angular build with `outputHashing: 'all'`, so a mismatch means
 * a deploy happened.
 *
 * On detection we set `stale` to `true`. The shell renders a banner
 * with a Reload button — we do NOT auto-reload, because silent reloads
 * can lose in-progress work (open modals, form drafts). The stream
 * completes via `takeWhile` once stale is set, so polling stops cleanly
 * instead of firing no-op intervals for the rest of the session.
 *
 * Triggers:
 *   - Immediately on `start()` (via `startWith`).
 *   - Every 5 minutes thereafter (`interval`).
 *   - On `visibilitychange` when the tab returns to the foreground —
 *     the most common moment a user comes back to a long-open tab.
 *   - On `online` when the browser reports the network is back, so a
 *     user who was offline during a deploy catches up immediately.
 * `exhaustMap` dedupes overlapping triggers so a visibility-fire mid-
 * poll doesn't spawn a duplicate request.
 *
 * Each `fetch` is bounded by `AbortSignal.timeout(10_000)` so a hung
 * connection doesn't pin a pending promise across poll intervals.
 *
 * In development builds the bundle filename has no hash, so
 * `detectCurrentMainBundle()` returns `null` and `start()` no-ops —
 * the regex deliberately requires the `-` separator that only
 * production hashing emits. A `console.warn` is logged when no hashed
 * bundle is found on a non-localhost origin so a regression in
 * `angular.json` (`outputHashing` removed) doesn't silently disable
 * deploy detection in production.
 *
 * `start()` is invoked once from `provideAppInitializer` in
 * `app.config.ts` rather than from a component lifecycle hook, which
 * is the Angular 20+ idiom for app-boot side effects and keeps the
 * service trivially mockable in unit tests (just don't call `start`).
 */
@Injectable({ providedIn: 'root' })
export class BuildVersionCheckService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _stale = signal(false);

  /** Becomes `true` once a newer deploy is detected on the server. */
  readonly stale = this._stale.asReadonly();

  private readonly currentMainBundle = this.detectCurrentMainBundle();

  /**
   * Tracks whether `check()` has run yet. On the very first run we
   * try to auto-reload silently (the user just opened the tab, no
   * in-progress work to lose). Subsequent runs surface the banner
   * so a mid-session deploy doesn't yank the user's form data.
   *
   * Why this matters: users whose `index.html` was cached before
   * `Cache-Control: no-store` was applied (pre-`9ad678c`) keep
   * loading the old bundle for up to a year. Since `--delete` is
   * also gone from the deploy workflow now, the old chunks
   * referenced by their cached `index.html` still resolve, so the
   * chunk-error recovery in `index.html` never fires either.
   * Without an auto-reload, they sit on the old bundle until they
   * notice and click the banner (which non-technical users
   * routinely do not).
   */
  private isFirstCheck = true;

  start(): void {
    if (!this.currentMainBundle) {
      // Defensive: on a non-localhost origin this signals a build-config
      // regression — `outputHashing: 'all'` was removed from
      // `angular.json` and deploy detection is now silently inactive.
      // localhost dev runs are unhashed by design, so they're skipped.
      const host = this.document.location.hostname;
      if (host && host !== 'localhost' && host !== '127.0.0.1' && host !== '::1') {
        console.warn(
          '[BuildVersionCheckService] No hashed main bundle detected — ' +
            'deploy detection is inactive. Check `outputHashing` in angular.json.'
        );
      }
      return;
    }

    const visible$ = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible')
    );
    const win = this.document.defaultView;
    const online$ = win ? fromEvent(win, 'online') : merge();

    merge(visible$, online$, interval(POLL_INTERVAL_MS))
      .pipe(
        startWith(0),
        exhaustMap(() => from(this.check())),
        // Stop polling once we've notified the shell — there's no
        // newer state to discover, and the user is about to reload.
        takeWhile(() => !this._stale(), true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async check(): Promise<void> {
    if (this._stale()) return;
    try {
      const res = await fetch('/index.html', {
        cache: 'reload',
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return;
      const html = await res.text();
      const deployed = this.extractMainBundleFromHtml(html);

      // Consume `isFirstCheck` ONLY after a successful fetch + parse.
      // If the first attempt fails (offline at boot, network blip,
      // timeout), we want the NEXT successful check — typically
      // triggered by the visibility/online events — to still be
      // eligible for auto-reload. Setting the flag before the await
      // would burn it on a failure that never observed the deployed
      // hash, downgrading those users to the banner-only path.
      const wasFirstCheck = this.isFirstCheck;
      this.isFirstCheck = false;

      if (!deployed || deployed === this.currentMainBundle) return;

      // First-boot auto-reload. Routes through the same
      // `__recoveryGuard` + `__reloadWithFreshShell` helpers in
      // `index.html` that SW cleanup and chunk-error recovery use,
      // so loop protection and cache-busting are shared. The
      // `'buildVersionMismatch'` key is the third recovery path;
      // the guard's 30s window prevents back-to-back reloads if
      // the detected hash doesn't shift after the fresh-shell
      // fetch (e.g., a CDN edge serving the wrong body) — that
      // case falls through to the banner.
      if (wasFirstCheck && this.tryAutoReload()) return;

      this._stale.set(true);
    } catch {
      // Network blip, timeout, or AbortError — try again on the
      // next tick. `isFirstCheck` is NOT consumed on this path,
      // so the next successful check still gets the auto-reload
      // opportunity.
    }
  }

  private tryAutoReload(): boolean {
    const win = this.document.defaultView as unknown as {
      __recoveryGuard?: (key: string) => boolean;
      __reloadWithFreshShell?: () => void;
    } | null;
    if (!win?.__recoveryGuard || !win.__reloadWithFreshShell) return false;
    if (!win.__recoveryGuard('buildVersionMismatch')) return false;
    win.__reloadWithFreshShell();
    return true;
  }

  /**
   * Scans the page's `<script>` tags for the hashed main bundle Angular
   * emitted at build time. Restricted to same-origin URLs and to scripts
   * whose final path segment matches the bundle pattern, so a
   * third-party script whose URL happens to contain a similar substring
   * can't false-positive.
   */
  private detectCurrentMainBundle(): string | null {
    const origin = this.document.location.origin;
    for (const script of Array.from(this.document.scripts)) {
      const src = script.src;
      if (!src || !src.startsWith(origin)) continue;
      const filename = filenameOf(src);
      if (filename && HASHED_MAIN_BUNDLE.test(filename)) return filename;
    }
    return null;
  }

  /**
   * Pulls the hashed main bundle filename out of a freshly-fetched
   * `index.html`. Anchored on the `/` or `"` boundary that precedes it
   * in real Angular output (`src="main-XXX.js"`, `href="main-XXX.js"`,
   * or `/main-XXX.js`), so any incidental occurrence elsewhere in the
   * markup can't be picked up by mistake.
   */
  private extractMainBundleFromHtml(html: string): string | null {
    const match = html.match(/[/"](main-[A-Z0-9]+\.js)/i);
    return match ? match[1] : null;
  }
}

function filenameOf(url: string): string | null {
  try {
    const { pathname } = new URL(url);
    const idx = pathname.lastIndexOf('/');
    return idx === -1 ? pathname : pathname.slice(idx + 1);
  } catch {
    return null;
  }
}
